/* eslint-disable react/no-array-index-key */
import type { ReactElement } from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

import { PDF_COLORS, PDF_FONT_FAMILY } from './styles';

import type { Overviews } from '../../overviewattck/types';
import { getEntries } from '@/utils';

export interface ChipGroup {
  label: string;
  values: Array<string>;
}

interface ReportFooterProps {
  threatInfo?: Array<ChipGroup>;
  attckOverviews?: Overviews | null;
  iocs?: Array<string>;
}

const ATTCK_COLUMNS: Array<{ key: 'taxonomy' | 'tactic' | 'name'; label: string; width: string }> =
  [
    { key: 'taxonomy', label: 'Taxonomy', width: '20%' },
    { key: 'tactic', label: 'Mitre Tactic', width: '35%' },
    { key: 'name', label: 'Name', width: '45%' },
  ];

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 30,
    paddingTop: 12,
    paddingBottom: 0,
  },
  // Each section is its own self-contained panel. By avoiding a wrapping
  // parent View with backgroundColor, we prevent the gray fill from being
  // stretched across the page break when a wrap={false} section gets
  // pushed to the next page.
  panel: {
    backgroundColor: PDF_COLORS.footerPanelBg,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  panelGap: {
    marginTop: 4,
  },
  sectionHeader: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 11,
    color: PDF_COLORS.footerHeader,
    marginBottom: 8,
  },
  sectionHeaderUpper: {
    textTransform: 'uppercase',
  },
  chipsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  chipGroup: {
    flexGrow: 1,
    flexBasis: '30%',
    minWidth: 140,
  },
  chipLabel: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 10,
    color: PDF_COLORS.footerLabel,
    marginBottom: 5,
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  chip: {
    backgroundColor: PDF_COLORS.footerChipBg,
    color: PDF_COLORS.footerChipText,
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 500,
    fontSize: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 3,
    marginRight: 4,
    marginBottom: 4,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  tableHeaderCell: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 10,
    color: PDF_COLORS.footerLabel,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 3,
  },
  tableCell: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 500,
    fontSize: 10,
    color: PDF_COLORS.footerAccent,
  },
  iocText: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 500,
    fontSize: 10,
    color: PDF_COLORS.footerAccent,
    lineHeight: 1.6,
  },
});

const cleanList = (values: Array<string> | undefined | null): Array<string> =>
  (values ?? []).map((v) => String(v).trim()).filter((v) => v.length > 0);

const overviewsToRows = (
  overviews: Overviews | undefined | null
): Array<{ taxonomy: string; tactic: string; name: string }> => {
  if (!overviews) return [];
  return getEntries(overviews).flatMap(([, value]) =>
    value.map((overview) => ({
      taxonomy: overview.taxonomy ?? '',
      tactic: overview.mitre_kill_chain?.join(', ') ?? '',
      name: overview.mitre_name ?? '',
    }))
  );
};

const ReportFooter = ({ threatInfo, attckOverviews, iocs }: ReportFooterProps) => {
  const renderableGroups = (threatInfo ?? [])
    .map((g) => ({ label: g.label, values: cleanList(g.values) }))
    .filter((g) => g.values.length > 0);

  const attckRows = overviewsToRows(attckOverviews);
  const iocList = cleanList(iocs);

  const panels: Array<ReactElement> = [];

  const pushPanel = (key: string, body: ReactElement, allowWrap = false) => {
    const isFirst = panels.length === 0;
    panels.push(
      <View
        key={key}
        style={isFirst ? styles.panel : [styles.panel, styles.panelGap]}
        wrap={allowWrap}
      >
        {body}
      </View>
    );
  };

  if (renderableGroups.length > 0) {
    pushPanel(
      'threat-info',
      <>
        <Text style={[styles.sectionHeader, styles.sectionHeaderUpper]}>Threat Information</Text>
        <View style={styles.chipsGrid}>
          {renderableGroups.map((group) => (
            <View key={group.label} style={styles.chipGroup}>
              <Text style={styles.chipLabel}>{group.label}</Text>
              <View style={styles.chipsRow}>
                {group.values.map((value, idx) => (
                  <Text key={`${group.label}-${idx}-${value}`} style={styles.chip}>
                    {value}
                  </Text>
                ))}
              </View>
            </View>
          ))}
        </View>
      </>
    );
  }

  if (attckRows.length > 0) {
    pushPanel(
      'attck-overview',
      <>
        <Text style={styles.sectionHeader}>Overview ATT&amp;CK</Text>
        <View style={styles.tableHeaderRow}>
          {ATTCK_COLUMNS.map((col) => (
            <View key={col.key} style={{ width: col.width }}>
              <Text style={styles.tableHeaderCell}>{col.label}</Text>
            </View>
          ))}
        </View>
        {attckRows.map((row, index) => (
          <View key={`attck-row-${index}`} style={styles.tableRow}>
            {ATTCK_COLUMNS.map((col) => (
              <View key={col.key} style={{ width: col.width }}>
                <Text style={styles.tableCell}>{row[col.key] || 'N/A'}</Text>
              </View>
            ))}
          </View>
        ))}
      </>,
      true // long tables may span multiple pages
    );
  }

  if (iocList.length > 0) {
    // Single wrap=true panel with a single <Text> body. The text engine
    // paginates line-by-line, which is the only structure that renders
    // correctly across multiple pages. For this to work the IoC panel
    // MUST be the last sibling on the PDF page — react-pdf's pagination
    // resolver compresses a multi-page wrapping element when it has any
    // sibling after it (issues diegomura/react-pdf#2436, #2922, #1676).
    const isFirstPanel = panels.length === 0;
    panels.push(
      <View key="iocs" style={isFirstPanel ? styles.panel : [styles.panel, styles.panelGap]} wrap>
        <Text style={styles.sectionHeader}>IoC Indicators</Text>
        <Text style={styles.iocText}>{iocList.join('\n')}</Text>
      </View>
    );
  }

  if (panels.length === 0) {
    return null;
  }

  return <View style={styles.container}>{panels}</View>;
};

export default ReportFooter;
